import { useSystemConfig } from '@/lib/queries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';

/**
 * A banner an admin can raise across the whole buyer app without a deploy.
 *
 * §11: colour is semantic. Amber means something is degraded or running out;
 * red means something is blocked — a recall, an outage. The settings help text
 * says so explicitly, because a banner picked for emphasis rather than meaning
 * is exactly how a colour system stops working.
 */
export function AnnouncementBanner() {
  const { data } = useSystemConfig();
  const announcement = (data as AnnouncementConfig | undefined)?.announcement;

  if (!announcement?.enabled || !announcement.message) return null;

  const tone = announcement.tone ?? 'info';

  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-2 border-b px-4 py-2.5 text-[12px]',
        tone === 'info' && 'border-blue-tint bg-blue-tint text-blue-deep',
        tone === 'caution' && 'border-caution/20 bg-caution-tint text-caution-ink',
        tone === 'danger' && 'border-danger/20 bg-danger-tint text-danger',
      )}
    >
      <Icon name={tone === 'info' ? 'bell' : 'alert'} size={14} className="shrink-0" />
      <span className="font-medium">{announcement.message}</span>
      {announcement.link_url && announcement.link_label ? (
        <a
          href={announcement.link_url}
          className="ml-auto font-bold underline underline-offset-2"
        >
          {announcement.link_label}
        </a>
      ) : null}
    </div>
  );
}

interface AnnouncementConfig {
  announcement?: {
    enabled?: boolean;
    message?: string;
    tone?: 'info' | 'caution' | 'danger';
    link_url?: string;
    link_label?: string;
  };
}
